// AGC: Media Storage Service
// AGC: Firebase Storage upload/download for photos, videos, and audio
// AGC: Handles compression, thumbnail generation, and progress tracking

import { ref, uploadBytesResumable, getDownloadURL, deleteObject, UploadTask } from 'firebase/storage'
import { storage } from '../lib/firebase'
import type { MediaUploadProgress, LotNoteType } from '../types/lotNotes'

// AGC: Max file sizes
const MAX_PHOTO_SIZE = 5 * 1024 * 1024 // 5MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024 // 50MB
const MAX_AUDIO_SIZE = 10 * 1024 * 1024 // 10MB

// AGC: Video duration limit (2 minutes)
export const MAX_VIDEO_DURATION = 120 // seconds

/**
 * AGC: Compress image to reduce file size
 */
const compressImage = async (file: File | Blob, maxWidth: number = 1920): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const reader = new FileReader()
    
    reader.onload = (e) => {
      img.src = e.target?.result as string
    }
    
    reader.onerror = reject
    
    img.onload = () => {
      const canvas = document.createElement('canvas')
      let width = img.width
      let height = img.height
      
      // AGC: Scale down if too large
      if (width > maxWidth) {
        height = (height * maxWidth) / width
        width = maxWidth
      }
      
      canvas.width = width
      canvas.height = height
      
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Failed to get canvas context'))
        return
      }
      
      ctx.drawImage(img, 0, 0, width, height)
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Failed to compress image'))
          }
        },
        'image/jpeg',
        0.85 // 85% quality
      )
    }
    
    img.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * AGC: Generate thumbnail for video
 */
const generateVideoThumbnail = async (videoFile: File | Blob): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    
    if (!ctx) {
      reject(new Error('Failed to get canvas context'))
      return
    }
    
    video.preload = 'metadata'
    video.src = URL.createObjectURL(videoFile)
    
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(1, video.duration / 2) // AGC: Capture at 1s or middle
    }
    
    video.onseeked = () => {
      canvas.width = Math.min(video.videoWidth, 640)
      canvas.height = (video.videoHeight * canvas.width) / video.videoWidth
      
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(video.src)
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Failed to generate thumbnail'))
          }
        },
        'image/jpeg',
        0.7
      )
    }
    
    video.onerror = () => {
      URL.revokeObjectURL(video.src)
      reject(new Error('Failed to load video'))
    }
  })
}

/**
 * AGC: Upload media file to Firebase Storage
 * AGC: Path format: media/{userId}/{noteId}/{filename}
 */
export const uploadMedia = async (
  file: File | Blob,
  type: LotNoteType,
  userId: string,
  noteId: string,
  onProgress?: (progress: MediaUploadProgress) => void
): Promise<{ url: string; thumbnailUrl?: string; size: number; duration?: number }> => {
  
  // AGC: Validate file size
  const fileSize = file.size
  if (type === 'photo' && fileSize > MAX_PHOTO_SIZE) {
    throw new Error('Photo file size exceeds 5MB limit')
  }
  if (type === 'video' && fileSize > MAX_VIDEO_SIZE) {
    throw new Error('Video file size exceeds 50MB limit')
  }
  if (type === 'audio' && fileSize > MAX_AUDIO_SIZE) {
    throw new Error('Audio file size exceeds 10MB limit')
  }
  
  // AGC: Compress images
  let uploadFile = file
  if (type === 'photo') {
    try {
      uploadFile = await compressImage(file)
      console.log(`🖼️ AGC: Compressed photo from ${fileSize} to ${uploadFile.size} bytes`)
    } catch (error) {
      console.warn('AGC: Image compression failed, uploading original', error)
    }
  }
  
  // AGC: Generate filename
  const timestamp = Date.now()
  const ext = type === 'photo' ? 'jpg' : type === 'video' ? 'mp4' : 'webm'
  const filename = `${type}_${timestamp}.${ext}`
  const filePath = `media/${userId}/${noteId}/${filename}`
  
  // AGC: Create storage reference
  const storageRef = ref(storage, filePath)

  // AGC: Set explicit content type for Storage rules validation
  const contentType = type === 'photo' ? 'image/jpeg'
    : type === 'video' ? 'video/mp4'
    : type === 'audio' ? 'audio/webm'
    : uploadFile.type || 'application/octet-stream'

  // AGC: Upload with progress tracking
  const uploadTask: UploadTask = uploadBytesResumable(storageRef, uploadFile, { contentType })
  
  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        
        if (onProgress) {
          onProgress({
            noteId,
            fileName: filename,
            progress,
            bytesTransferred: snapshot.bytesTransferred,
            totalBytes: snapshot.totalBytes,
            status: 'uploading',
          })
        }
        
        console.log(`📤 AGC: Upload ${progress.toFixed(1)}% complete`)
      },
      (error) => {
        console.error('AGC: Upload failed', error)
        if (onProgress) {
          onProgress({
            noteId,
            fileName: filename,
            progress: 0,
            bytesTransferred: 0,
            totalBytes: file.size,
            status: 'failed',
            error: error.message,
          })
        }
        reject(error)
      },
      async () => {
        // AGC: Upload complete
        try {
          const url = await getDownloadURL(uploadTask.snapshot.ref)
          
          let thumbnailUrl: string | undefined
          let duration: number | undefined
          
          // AGC: Generate thumbnail for video
          if (type === 'video') {
            try {
              const thumbnail = await generateVideoThumbnail(file)
              const thumbPath = `media/${userId}/${noteId}/thumbnail.jpg`
              const thumbRef = ref(storage, thumbPath)
              const thumbTask = await uploadBytesResumable(thumbRef, thumbnail)
              thumbnailUrl = await getDownloadURL(thumbTask.ref)
              console.log('🎬 AGC: Video thumbnail generated')
              
              // AGC: Get video duration
              const video = document.createElement('video')
              video.src = URL.createObjectURL(file)
              await new Promise((res) => {
                video.onloadedmetadata = () => {
                  duration = video.duration
                  URL.revokeObjectURL(video.src)
                  res(null)
                }
              })
            } catch (error) {
              console.warn('AGC: Thumbnail generation failed', error)
            }
          }
          
          // AGC: Get audio duration
          if (type === 'audio') {
            try {
              const audio = document.createElement('audio')
              audio.src = URL.createObjectURL(file)
              await new Promise((res) => {
                audio.onloadedmetadata = () => {
                  duration = audio.duration
                  URL.revokeObjectURL(audio.src)
                  res(null)
                }
              })
            } catch (error) {
              console.warn('AGC: Audio duration detection failed', error)
            }
          }
          
          if (onProgress) {
            onProgress({
              noteId,
              fileName: filename,
              progress: 100,
              bytesTransferred: uploadFile.size,
              totalBytes: uploadFile.size,
              status: 'completed',
            })
          }
          
          console.log('✅ AGC: Upload complete:', url)
          
          resolve({
            url,
            thumbnailUrl,
            size: uploadFile.size,
            duration,
          })
        } catch (error) {
          console.error('AGC: Failed to get download URL', error)
          reject(error)
        }
      }
    )
  })
}

/**
 * AGC: Delete media file from Firebase Storage
 */
export const deleteMedia = async (mediaUrl: string): Promise<void> => {
  try {
    const fileRef = ref(storage, mediaUrl)
    await deleteObject(fileRef)
    console.log('🗑️ AGC: Media deleted:', mediaUrl)
  } catch (error) {
    console.error('AGC: Failed to delete media', error)
    throw error
  }
}

/**
 * AGC: Get media URL from storage path
 */
export const getMediaUrl = async (storagePath: string): Promise<string> => {
  try {
    const fileRef = ref(storage, storagePath)
    const url = await getDownloadURL(fileRef)
    return url
  } catch (error) {
    console.error('AGC: Failed to get media URL', error)
    throw error
  }
}

/**
 * AGC: Validate video duration
 */
export const validateVideoDuration = async (videoFile: File | Blob): Promise<boolean> => {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    video.src = URL.createObjectURL(videoFile)
    
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src)
      const isValid = video.duration <= MAX_VIDEO_DURATION
      if (!isValid) {
        console.warn(`⚠️ AGC: Video duration ${video.duration}s exceeds ${MAX_VIDEO_DURATION}s limit`)
      }
      resolve(isValid)
    }
    
    video.onerror = () => {
      URL.revokeObjectURL(video.src)
      resolve(false)
    }
  })
}
